import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Table, Tag, Tooltip} from 'antd';
import styles from './Downloads.module.css';
import tableStyles from '../../../components/UTable/UTable.module.css';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import useApi from '../../../hooks/useApi.hook';
import useOnPageReturn from '../../../hooks/useOnPageReturn.hook';
import {PREFIX, downloadLinkUrl, downloadUrl, downloadsUrl} from '../../../helpers/constants';
import {notifyDownloadsChanged} from '../../../helpers/downloadsEvents';

const POLL_MS = 2500;
// A task queued through MCP raises no browser event, so an open page keeps asking.
const IDLE_POLL_MS = 15000;
const ACTIVE = ['pending', 'processing'];

const STATUS_TAGS = {
	pending: {color: 'default', label: 'В очереди'},
	processing: {color: 'processing', label: 'Формируется'},
	ready: {color: 'success', label: 'Готов'},
	failed: {color: 'error', label: 'Ошибка'}
};

const formatDate = (value) => new Date(value).toLocaleString('ru-RU', {
	day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
});

const formatSize = (bytes) => {
	if (bytes == null) return '—';
	if (bytes < 1024) return `${bytes} Б`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
	return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

const formatExpires = (value) => {
	const hours = Math.max(0, Math.round((new Date(value) - Date.now()) / 3600000));
	if (hours < 1) return 'меньше часа';
	if (hours < 48) return `через ${hours} ч`;
	return `через ${Math.round(hours / 24)} дн`;
};

const errorText = (e, fallback) => e?.response?.data?.detail || fallback;

export function Downloads() {
	const api = useApi();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(undefined);
	const timer = useRef(null);

	const fetchRows = useCallback(async () => {
		clearTimeout(timer.current);
		try {
			const {data} = await api.get(downloadsUrl);
			setRows(data);
			if (!document.hidden) {
				const active = data.some((row) => ACTIVE.includes(row.status));
				timer.current = setTimeout(fetchRows, active ? POLL_MS : IDLE_POLL_MS);
			}
		} catch (e) {
			setError(errorText(e, 'Не удалось загрузить список'));
		} finally {
			setLoading(false);
		}
	}, [api]);

	useEffect(() => {
		fetchRows();
		return () => clearTimeout(timer.current);
	}, [fetchRows]);

	useOnPageReturn(fetchRows);

	const handleDownload = async (row) => {
		try {
			const {data} = await api.post(downloadLinkUrl(row.id));
			const link = document.createElement('a');
			link.href = `${PREFIX}${data.url}`;
			link.click();
			setRows((prev) => prev.map((r) => (
				r.id === row.id ? {...r, seen_at: r.seen_at || new Date().toISOString()} : r
			)));
			notifyDownloadsChanged();
		} catch (e) {
			setError(errorText(e, 'Не удалось скачать файл'));
		}
	};

	const handleDelete = async (row) => {
		try {
			await api.delete(downloadUrl(row.id));
			setRows((prev) => prev.filter((r) => r.id !== row.id));
			notifyDownloadsChanged();
		} catch (e) {
			setError(errorText(e, 'Не удалось удалить файл'));
		}
	};

	const showAuthor = useMemo(
		() => new Set(rows.map((row) => row.user_id)).size > 1,
		[rows]
	);

	const columns = [
		{
			title: 'Название',
			dataIndex: 'title',
			width: '35%',
			render: (title, row) => (
				<span className={row.status === 'ready' && !row.seen_at ? styles.fresh : undefined}>
					{title}
					{row.file_name && <div className={styles.fileName}>{row.file_name}</div>}
				</span>
			)
		},
		...(showAuthor ? [{title: 'Кто сформировал', dataIndex: 'user_name', render: (name) => name || '—'}] : []),
		{title: 'Создан', dataIndex: 'created_at', width: 150, render: formatDate},
		{
			title: 'Статус',
			dataIndex: 'status',
			width: 130,
			render: (status, row) => {
				const tag = <Tag color={STATUS_TAGS[status]?.color}>{STATUS_TAGS[status]?.label || status}</Tag>;
				return status === 'failed' && row.error ? <Tooltip title={row.error}>{tag}</Tooltip> : tag;
			}
		},
		{title: 'Размер', dataIndex: 'size', width: 100, render: formatSize},
		{title: 'Удалится', dataIndex: 'expires_at', width: 120, render: formatExpires},
		{
			title: 'Скачать',
			key: 'download',
			width: 90,
			render: (_, row) => row.status === 'ready' && (
				<button type="button" className={styles.action} title="Скачать" onClick={() => handleDownload(row)}>
					<img src="/icons/up-icon.svg" alt="Скачать" className={styles.downloadIcon} />
				</button>
			)
		},
		{
			title: 'Удалить',
			key: 'delete',
			width: 90,
			render: (_, row) => (
				<button type="button" className={styles.action} title="Удалить" onClick={() => handleDelete(row)}>
					<img src="/icons/delete-icon.svg" alt="Удалить" />
				</button>
			)
		}
	].map((column) => (column.dataIndex === 'title' ? column : {...column, align: 'center'}));

	return (
		<div className={`${styles.downloads} ${styles.table}`}>
			<ErrorMessage error={error} onClose={() => setError(undefined)} />
			<div className={tableStyles.tableWrapper}>
				<Table
					size="small"
					className={tableStyles.customTable}
					columns={columns}
					dataSource={rows}
					loading={loading}
					rowKey="id"
					pagination={false}
					locale={{emptyText: 'Здесь появятся файлы, которые вы сформируете'}}
				/>
			</div>
		</div>
	);
}
